import React from 'react'
import classNames from 'classnames'
import { Base, Icon, Text, Button } from '@crossfit/ui'
import { format, parse } from 'date-fns'

import { useStripeCheckoutPortal, useStripePaymentPortal } from '../../hooks'
import style from './style.module.css'

type Props = React.ComponentProps<typeof Base>

function NotificationBanner({ className, ...props }: Props) {
  return <Base className={classNames(style.wrapper, className)} {...props} />
}

export function PaymentIssueNotificationBanner() {
  const [openStripePaymentPortal, isWaiting] = useStripePaymentPortal()

  return (
    <NotificationBanner className={style.banner}>
      <div className={style.text}>
        <Icon name='alert' className={style.icon} />
        <Text size='small'>
          We’ve run into an issue trying to process your latest payment.
        </Text>
      </div>
      <div className={style.buttons}>
        <Button
          variant='secondary'
          onClick={openStripePaymentPortal}
          disabled={isWaiting}
        >
          Update Payment Info
        </Button>
      </div>
    </NotificationBanner>
  )
}

export function AffiliateStandingIssueNotificationBanner() {
  return (
    <NotificationBanner className={style.banner}>
      <div className={style.text}>
        <Icon name='alert' className={style.icon} />
        <Text size='small'>
          Updates are required to maintain your affiliation status.
        </Text>
      </div>
      <div className={style.buttons}>
        <Button
          variant='secondary'
          as='a'
          href='mailto:affiliatesupport@crossfit.com'
          size='xsmall'
        >
          Contact Support
        </Button>
      </div>
    </NotificationBanner>
  )
}

export function ReactivateNotificationBanner() {
  const [openStripePaymentPortal, isWaiting] = useStripePaymentPortal()

  return (
    <NotificationBanner className={style.banner}>
      <div className={style.text}>
        <Text size='small'>We’d love to have you back!</Text>
      </div>
      <div className={style.buttons}>
        <Button
          variant='secondary'
          size='xsmall'
          onClick={openStripePaymentPortal}
          disabled={isWaiting}
        >
          Reactivate
        </Button>
        <Button
          variant='tertiary'
          as='a'
          href='mailto:affiliatesupport@crossfit.com'
          size='xsmall'
        >
          Contact Support
        </Button>
      </div>
    </NotificationBanner>
  )
}

type BetaUserNotificationBannerProps = {
  betaExpirationDate: string
  formattedPrice: string
}

export function BetaUserNotificationBanner({
  betaExpirationDate,
  formattedPrice,
}: BetaUserNotificationBannerProps) {
  const [openStripeCheckoutPortal, isWaiting] = useStripeCheckoutPortal()
  const expirationDate = format(
    parse(betaExpirationDate, 'yyyy-MM-dd', new Date()),
    'MMMM do',
  )

  return (
    <NotificationBanner className={style.banner}>
      <div className={style.text}>
        <Text size='small'>
          {expirationDate} will be the last free day of CAP. Subscribe for{' '}
          {formattedPrice}/mo to continue!
        </Text>
      </div>
      <div className={style.buttons}>
        <Button
          variant='secondary'
          size='xsmall'
          onClick={openStripeCheckoutPortal}
          disabled={isWaiting}
        >
          Subscribe
        </Button>
      </div>
    </NotificationBanner>
  )
}
